import { format } from "date-fns";
import { Tooltip } from "@heroui/tooltip";
import { motion } from "framer-motion";
import ToraAvatar from "../../../tora-components/ToraAvatar";
import { EnhancedChatMessage } from "@/types/chat";
import { LoadingDots } from "./LoadingDots";
import { MessageContent } from "./MessageContent";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import { User } from "@/types";

interface ChatMessageProps {
  message: EnhancedChatMessage;
  user: User;
  copyToClipboard: (messageId: string, text: string) => Promise<void>;
  awaitMessage: string;
  flightSearchMessage: string;
}

const ChatMessage = ({
  message,
  user,
  copyToClipboard,
  awaitMessage,
  flightSearchMessage,
}: ChatMessageProps) => {
  const isLoading = message.status === "sending" && message.text === "";

  const messageStyles = {
    user: "bg-indigo-700 text-white p-3 2xl:text-[13.5px]",
    assistant: "bg-neutral-100 text-neutral-800 px-3 py-2 2xl:text-[13.5px]",
    loading: "relative",
  };

  const getMessageStyle = () => {
    if (message.isUser) return messageStyles.user;
    if (isLoading) return messageStyles.loading;
    return messageStyles.assistant;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-start space-x-2 ${
        message.isUser ? "flex-row-reverse space-x-reverse" : "flex-row"
      }`}
      key={message.id}
    >
      {!message.isUser && <ToraAvatar />}

      <div className="flex flex-col max-w-[60%]">
        <div className={`${getMessageStyle()} rounded-xl`}>
          {isLoading ? (
            <LoadingDots
              awaitMessage={awaitMessage}
              flightSearchMessage={flightSearchMessage}
            />
          ) : (
            <MessageContent isUser={message.isUser} text={message.text} />
          )}
        </div>

        <div className="flex items-center mt-1 space-x-2">
          <span className="text-[10px] 2xl:text-[11px] text-neutral-500 font-light">
            {format(message.timestamp, "h:mm a")}
          </span>

          {!message.isUser && message.status === "sent" && (
            <Tooltip
              className="rounded-md"
              placement="right"
              showArrow={true}
              content={
                <p className="text-xs 2xl:text-[11px] font-light text-neutral-800">
                  {message.copied ? "Copied!" : "Copy to clipboard"}
                </p>
              }
            >
              <button
                onClick={() => copyToClipboard(message.id, message.text)}
                className="text-neutral-400 hover:text-neutral-700 2xl:text-[13px]"
              >
                <i
                  className={`fas ${message.copied ? "fa-check" : "fa-copy"}`}
                />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ChatMessage;
